import React from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { DateTime } from "luxon";
import { Line } from "react-chartjs-2";
import { CHART_COLORS } from "../../constants/chartColors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, annotationPlugin);

type LineChartPoint = {
  originalDate?: string;
  x: string;
  y: number | null;
};

type LineChartDataset = {
  backgroundColor?: string;
  borderColor: string;
  data: LineChartPoint[];
  label: string;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  pointHoverBackgroundColor?: string;
  pointHoverBorderColor?: string;
};

type LineChartData = {
  datasets: LineChartDataset[];
  labels: string[];
};

export type LineChartProps = {
  data: LineChartData;
  height?: number;
  optimizationDates?: string[]; // Array of dates (labels) where vertical lines should be drawn
  optimizationLabel?: string; // Label for optimization lines in legend
  optimizationLegendContent?: React.ReactNode; // Custom React content for optimization legend item (enables clickable parts)
  width?: number;
};

const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 300,
  optimizationDates = [],
  optimizationLabel = "Optimizations",
  optimizationLegendContent,
}) => {
  const [showOptimizations, setShowOptimizations] = React.useState(true);
  const [hiddenDatasets, setHiddenDatasets] = React.useState<Set<number>>(new Set());
  const chartRef = React.useRef<ChartJS<"line", LineChartPoint[]> | undefined>(undefined);
  const hasCustomOptimizationLegend = !!optimizationLegendContent && optimizationDates.length > 0;

  // Create annotations for vertical lines (only if toggled on)
  const annotations: Record<string, unknown> = {};
  if (showOptimizations) {
    optimizationDates.forEach((label, index) => {
      annotations[`line${index}`] = {
        type: "line",
        xMin: label,
        xMax: label,
        borderColor: CHART_COLORS.optimizationMarker,
        borderWidth: 2,
      };
    });
  }

  // Add optimization dataset with data points for tooltips
  const chartData = { ...data };
  if (optimizationDates.length > 0) {
    // Create data points for each optimization date
    // Use the maximum y value from existing data to position points at the top
    const maxYValue = Math.max(...data.datasets.flatMap((ds) => ds.data.map((point) => point.y || 0)));

    const optimizationDataPoints = data.labels.map((label) => {
      // Check if this label matches any optimization date
      const isOptimizationDate = optimizationDates.includes(label);
      return {
        x: label,
        y: isOptimizationDate ? maxYValue : null, // Only add point at optimization dates
      };
    });

    const optimizationDataset = {
      label: optimizationLabel,
      data: optimizationDataPoints,
      borderColor: CHART_COLORS.optimization,
      backgroundColor: CHART_COLORS.optimization,
      pointBackgroundColor: CHART_COLORS.optimization, // Transparent points
      pointBorderColor: "rgba(0, 0, 0, 0)", // Transparent border
      pointHoverBackgroundColor: "rgba(0, 0, 0, 0)", // Keep transparent on hover
      pointHoverBorderColor: "rgba(0, 0, 0, 0)", // Keep transparent on hover
      pointRadius: 0, // Large hit area for easier hovering
      showLine: false, // Don't draw line between points
      hidden: !showOptimizations,
    };

    // Insert at the end of datasets (so it appears last in legend)
    if (data.datasets.length > 0) {
      chartData.datasets = [...data.datasets, optimizationDataset];
    } else {
      chartData.datasets = [optimizationDataset];
    }
  }

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    elements: {
      line: {
        tension: 0, // smooth curves!
        borderWidth: 2,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      annotation: {
        annotations: annotations as never,
        common: {
          drawTime: "afterDatasetsDraw",
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || "";
            const value = context.parsed.y;
            const raw = context.raw as { originalDate?: string; x?: string };
            const originalDate = raw.originalDate;

            // Check if this is an optimization dataset
            if (datasetLabel === optimizationLabel) {
              // Show the date for optimization points
              return `${datasetLabel}: ${raw.x || context.label}`;
            }

            const formattedDate = originalDate ? ` (${DateTime.fromISO(originalDate).toFormat("dd. MM. yyyy")})` : "";

            return `${datasetLabel}: ${value ?? "-"}${formattedDate}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  const toggleOptimizationVisibility = () => {
    setShowOptimizations((prev) => !prev);
    const chart = chartRef.current;
    if (chart) {
      const optIndex = chart.data.datasets.findIndex((ds) => ds.label === optimizationLabel);
      if (optIndex >= 0) {
        if (chart.isDatasetVisible(optIndex)) {
          chart.hide(optIndex);
        } else {
          chart.show(optIndex);
        }
        chart.update();
      }
    }
  };

  const toggleDatasetVisibility = (index: number) => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const dataset = chart.data.datasets[index];
    if (dataset?.label === optimizationLabel) {
      toggleOptimizationVisibility();
      return;
    }

    setHiddenDatasets((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        chart.show(index);
      } else {
        next.add(index);
        chart.hide(index);
      }
      chart.update();
      return next;
    });
  };

  const legendItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    width: "calc(50% - 8px)",
  };

  return (
    <div>
      <div style={{ height }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          maxWidth: "780px",
          margin: "0 auto",
          justifyContent: "flex-start",
          gap: "16px 16px",
          paddingTop: "10px",
          fontSize: "12px",
          fontFamily: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
        }}
      >
        {chartData.datasets.map((dataset, index) => {
          const isOptimization = dataset.label === optimizationLabel;

          if (isOptimization && hasCustomOptimizationLegend) {
            return (
              <div key="optimization" style={legendItemStyle} onClick={toggleOptimizationVisibility}>
                <span
                  style={{
                    display: "inline-block",
                    width: "25px",
                    height: "10px",
                    backgroundColor: dataset.borderColor,
                  }}
                />
                <span
                  style={{
                    color: "var(--app-text-muted)",
                    textDecoration: showOptimizations ? "none" : "line-through",
                  }}
                >
                  {optimizationLegendContent}
                </span>
              </div>
            );
          }

          const isHidden = isOptimization ? !showOptimizations : hiddenDatasets.has(index);

          return (
            <div key={dataset.label} style={legendItemStyle} onClick={() => toggleDatasetVisibility(index)}>
              <span
                style={{
                  display: "inline-block",
                  width: "25px",
                  height: "10px",
                  backgroundColor: dataset.borderColor,
                }}
              />
              <span style={{ color: "var(--app-text-muted)", textDecoration: isHidden ? "line-through" : "none" }}>
                {dataset.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LineChart;
